import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import usePushSubscription from "../lib/usePushSubscription";
import { toast } from "sonner";
import { Bell, CheckCheck, Inbox, BellRing, BellOff } from "lucide-react";
import useAccessiblePopover from "@/hooks/useAccessiblePopover";

/** Topbar notification bell: shows the per-user inbox populated by
 *  smart routing (inquiry/booking/incident/etc.) */
export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);
  const push = usePushSubscription();
  const { panelRef, triggerRef, closeAndRestore } = useAccessiblePopover(open, () => setOpen(false));

  const load = async () => {
    try {
      const { data: d } = await api.get("/notifications/inbox?limit=20");
      setData(d);
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 45_000);
    return () => clearInterval(pollRef.current);
  }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/inbox/${id}/read`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markAllRead = async () => {
    try {
      const { data: d } = await api.post("/notifications/inbox/mark-all-read");
      toast.success(`Marked ${d.marked} as read`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        id="notification-bell-trigger"
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="notif-bell-btn"
        className="relative p-2 rounded-md hover:bg-slate-100"
        title="Notifications"
        aria-label="Notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="notification-popover"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {data.unread_count > 0 && (
          <span
            data-testid="notif-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center px-1"
          >
            {data.unread_count > 99 ? "99+" : data.unread_count}
          </span>
        )}
      </button>
      {open && (
        <>
          <button type="button" tabIndex={-1} className="fixed inset-0 z-30" onClick={closeAndRestore} aria-hidden="true" data-popover-backdrop />
          <div
            ref={panelRef}
            id="notification-popover"
            role="dialog"
            aria-modal="false"
            aria-labelledby="notification-popover-title"
            className="absolute right-0 mt-2 w-96 max-w-[92vw] rounded-xl border border-slate-200 bg-white shadow-xl z-40 overflow-hidden"
            data-testid="notif-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-slate-500" />
                <div id="notification-popover-title" className="font-semibold text-sm">Notifications</div>
                <div className="text-xs text-slate-500">({data.unread_count} unread)</div>
              </div>
              {data.unread_count > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  data-testid="notif-mark-all-read"
                  className="text-xs text-[#1B4332] hover:underline inline-flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
            </div>
            {data.items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="notif-empty">
                <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                Nothing routed to you yet.
                <br />
                <span className="text-xs text-slate-400">
                  When work matches your skills and you&apos;re clocked-in, you&apos;ll see it here.
                </span>
              </div>
            ) : (
              <ul className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
                {data.items.map((n) => (
                  <li key={n.id} data-testid={`notif-item-${n.id}`}>
                    <NotifRow notification={n} onRead={() => markRead(n.id)} onClose={() => setOpen(false)} />
                  </li>
                ))}
              </ul>
            )}
            <PushFooter push={push} />
          </div>
        </>
      )}
    </div>
  );
}

const KIND_LABELS = {
  inquiry_new: { tone: "border-l-sky-400 bg-sky-50/50", emoji: "✉" },
  in_app_booking_new: { tone: "border-l-emerald-400 bg-emerald-50/50", emoji: "▶" },
  booking_update: { tone: "border-l-emerald-400 bg-emerald-50/50", emoji: "▶" },
  customer_message: { tone: "border-l-sky-400 bg-sky-50/50", emoji: "✉" },
  vehicle_warning: { tone: "border-l-amber-400 bg-amber-50/50", emoji: "!" },
  doc_expiry: { tone: "border-l-amber-400 bg-amber-50/50", emoji: "!" },
  incident_new: { tone: "border-l-rose-400 bg-rose-50/50", emoji: "▲" },
  damage_new: { tone: "border-l-rose-400 bg-rose-50/50", emoji: "▲" },
  billing_alert: { tone: "border-l-violet-400 bg-violet-50/50", emoji: "$" },
  system_alert: { tone: "border-l-slate-400 bg-slate-50", emoji: "•" },
};

function NotifRow({ notification, onRead, onClose }) {
  const meta = KIND_LABELS[notification.notification_type] || KIND_LABELS.system_alert;
  const when = notification.created_at
    ? new Date(notification.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
    : "";
  const Wrapper = notification.link ? Link : "div";
  const wrapperProps = notification.link
    ? { to: notification.link, onClick: () => { onClose(); if (!notification.read) onRead(); } }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={`flex items-start gap-2 px-4 py-3 border-l-4 ${meta.tone} ${
        notification.read ? "opacity-65" : ""
      } hover:bg-slate-50/80 transition`}
    >
      <span className="text-base leading-none mt-0.5 select-none" aria-hidden="true">{meta.emoji}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{notification.title}</div>
        {notification.body && (
          <div className="text-xs text-slate-600 line-clamp-2 mt-0.5">{notification.body}</div>
        )}
        <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2">
          <span>{when}</span>
          {notification.routing?.fallback_used && (
            <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="notif-fallback-tag">
              Fallback to owner
            </span>
          )}
          {!notification.read && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRead(); }}
              data-testid="notif-mark-read-btn"
              className="ml-auto text-[10px] text-[#1B4332] hover:underline"
            >
              Mark read
            </button>
          )}
        </div>
      </div>
    </Wrapper>
  );
}


/** Footer in the bell dropdown: lets the user enable/disable real device
 *  push notifications for THIS browser/device. Each device is its own
 *  subscription, so an owner's phone + laptop can both be subscribed. */
function PushFooter({ push }) {
  const { status, enable, disable, sendTest } = push;
  if (status === "unsupported") {
    return (
      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500" data-testid="push-footer-unsupported">
        Device push notifications aren&apos;t supported in this browser. On iPhone/iPad, install the app to your Home Screen (iOS 16.4+) to enable push.
      </div>
    );
  }
  if (status === "disabled-by-server") {
    return (
      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500" data-testid="push-footer-disabled-server">
        Push delivery isn&apos;t configured on the server yet. You&apos;ll keep getting in-app notifications.
      </div>
    );
  }
  if (status === "blocked") {
    return (
      <div className="px-4 py-3 border-t border-slate-100 bg-amber-50 text-[11px] text-amber-900" data-testid="push-footer-blocked">
        <BellOff className="w-3 h-3 inline mr-1" />
        Notifications are <strong>blocked</strong> in your browser settings. Open the lock icon in the address bar, set Notifications to &ldquo;Allow&rdquo;, then reload.
      </div>
    );
  }
  if (status === "subscribed") {
    return (
      <div className="px-4 py-3 border-t border-slate-100 bg-emerald-50/40 flex items-center justify-between gap-2" data-testid="push-footer-subscribed">
        <span className="text-[11px] text-emerald-900 inline-flex items-center gap-1">
          <BellRing className="w-3.5 h-3.5" /> Push enabled on this device
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={async () => {
              const ok = await sendTest();
              toast[ok ? "success" : "error"](ok ? "Test push sent" : "Couldn't send test push");
            }}
            data-testid="push-test-btn"
            className="text-[11px] text-[#1B4332] hover:underline"
          >
            Test
          </button>
          <span className="text-slate-300">·</span>
          <button
            type="button"
            onClick={disable}
            data-testid="push-disable-btn"
            className="text-[11px] text-rose-700 hover:underline"
          >
            Disable
          </button>
        </div>
      </div>
    );
  }
  // default / loading
  return (
    <div className="px-4 py-3 border-t border-slate-100" data-testid="push-footer-default">
      <button
        type="button"
        onClick={enable}
        disabled={status === "loading"}
        data-testid="push-enable-btn"
        className="w-full px-3 py-2 rounded-lg btn-brand text-xs font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
      >
        <BellRing className="w-3.5 h-3.5" />
        {status === "loading" ? "Enabling…" : "Enable device push notifications"}
      </button>
      <p className="text-[10px] text-slate-500 mt-1.5 leading-snug">
        Get a buzz on your phone/laptop when a customer inquiry, booking request, or incident is routed to you.
        You&apos;ll still get in-app notifications without this.
      </p>
    </div>
  );
}
